// db.js — Firestore persistence for user data, plus event logging.
//
// Layout (schema version 2):
//   users/{uid}/tasks/{taskId}        one document per open task
//   users/{uid}/completed/{taskId}    one document per completed task
//   users/{uid}/fishbowl/meta         tags, sequential queue, streak, active task, schemaVersion
//   users/{uid}/fishbowl/state        LEGACY single-document state (schema 1). Read once to
//                                     migrate, then left untouched as a backup. Never deleted here.
//
// Saving is diff-based: saveUserData() compares what the app holds against what
// was last loaded from the cloud and writes only the documents that changed.

import { auth, db } from "./firebase.js";
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  collection,
  query,
  where,
  writeBatch,
  increment
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  SCHEMA_VERSION, normalizeTask, normalizeCompleted, legacyId, plain, durationSecOf
} from "./model.js";

// The main page loads only recent completed history; all-time counts live in
// meta.totals and are updated atomically with every save (see saveUserData).
export const HISTORY_WINDOW_DAYS = 90;
export function historyWindowStart() {
  return new Date(Date.now() - HISTORY_WINDOW_DAYS * 864e5).toISOString();
}

const BATCH_LIMIT = 400; // Firestore allows 500 operations per batch

// What the cloud held for the current user when last loaded/synced: id -> JSON.
// Diffing against this is what turns "save everything" into "save what changed".
let synced = { uid: null, tasks: new Map(), completed: new Map(), meta: null, streakInfo: null, totals: null, windowStart: null };

function resetSynced(uid) {
  synced = { uid, tasks: new Map(), completed: new Map(), meta: null, streakInfo: null, totals: null, windowStart: null };
}

// ✅ Exported: the all-time completed totals as last known from the cloud
export function getCloudTotals() {
  return synced.totals ? { ...synced.totals } : null;
}

// ✅ Exported: Get user ID
export function getUID() {
  return auth.currentUser ? auth.currentUser.uid : null;
}

// ✅ Exported: resolve once Firebase has settled the initial auth state (a
// signed-in user, anonymous or named). Pages that load data on their own —
// like the stats page — must await this first, or getUID() is still null and
// they fall back to the (windowed) local copy. Times out so it never hangs.
export function whenAuthReady(timeoutMs = 8000) {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise(resolve => {
    let settled = false;
    const finish = (u) => { if (settled) return; settled = true; resolve(u); };
    // The first callback is Firebase's answer for this browser: a restored
    // session (named or anonymous) or null. Either way auth has settled.
    let unsub = null;
    unsub = onAuthStateChanged(auth, (u) => { finish(u); if (typeof unsub === 'function') unsub(); });
    if (settled && typeof unsub === 'function') unsub(); // callback fired synchronously
    setTimeout(() => finish(auth.currentUser), timeoutMs);
  });
}

const byCreated = (a, b) => (a.createdOn || '').localeCompare(b.createdOn || '');
const byCompleted = (a, b) => (a.completedOn || '').localeCompare(b.completedOn || '');

function metaSubset(meta) {
  return {
    schemaVersion: SCHEMA_VERSION,
    tags: Array.isArray(meta?.tags) ? meta.tags : [],
    manualQueue: Array.isArray(meta?.manualQueue) ? meta.manualQueue : [],
    active: meta?.active || null,
    streakInfo: meta?.streakInfo || null,
    deletedIds: Array.isArray(meta?.deletedIds) ? meta.deletedIds : []
  };
}

// ---------------------------------------------------------------------------
// Migration: schema 1 (single state doc) -> schema 2 (per-item docs + meta).
// Idempotent: ids are stable (existing ids, or a deterministic hash for legacy
// records), every write is a set-by-id, and meta is written only after all
// item writes succeeded. The state document itself is never modified.
// ---------------------------------------------------------------------------
async function migrateStateDoc(uid, state) {
  const tasks = (state.tasks || [])
    .map((t, i) => normalizeTask(t, legacyId('task', t, i))).filter(Boolean);
  const completed = (state.completed || [])
    .map((c, i) => normalizeCompleted(c, legacyId('done', c, i))).filter(Boolean);
  const queue = (state.manualQueue || [])
    .map((q, i) => normalizeTask(q, legacyId('queue', q, i))).filter(Boolean);

  const ops = [
    ...tasks.map(t => ['tasks', t]),
    ...completed.map(c => ['completed', c])
  ];
  for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    ops.slice(i, i + BATCH_LIMIT).forEach(([coll, item]) => {
      batch.set(doc(db, 'users', uid, coll, item.id), plain(item));
    });
    await batch.commit();
  }

  const meta = {
    schemaVersion: SCHEMA_VERSION,
    tags: Array.isArray(state.tags) ? state.tags : [],
    manualQueue: queue,
    streakInfo: state.streakInfo || null,
    active: state.active || null,
    idsUpgraded: false,
    migratedFrom: 'fishbowl/state',
    migratedAt: new Date().toISOString(),
    migratedCounts: { tasks: tasks.length, completed: completed.length, queue: queue.length }
  };
  await setDoc(doc(db, 'users', uid, 'fishbowl', 'meta'), plain(meta), { merge: true });
  console.log(`Migrated Firestore data to schema ${SCHEMA_VERSION}:`, meta.migratedCounts);
}

// ✅ Exported: Load full user data
// Returns { tasks, completed, tags, manualQueue, streakInfo, active } or null for a brand-new user.
// options.all = true loads the full completed history instead of the recent window.
export async function loadUserData(options = {}) {
  const uid = getUID();
  if (!uid) return null;

  const metaRef = doc(db, 'users', uid, 'fishbowl', 'meta');
  let metaSnap = await getDoc(metaRef);
  let meta = metaSnap.exists() ? metaSnap.data() : null;

  if (!meta || meta.schemaVersion !== SCHEMA_VERSION) {
    const stateSnap = await getDoc(doc(db, 'users', uid, 'fishbowl', 'state'));
    if (stateSnap.exists()) {
      await migrateStateDoc(uid, stateSnap.data());
      metaSnap = await getDoc(metaRef);
      meta = metaSnap.exists() ? metaSnap.data() : null;
    } else if (!meta) {
      resetSynced(uid);
      return null; // nothing in the cloud yet
    }
  }

  // The history window is only safe once every record in the cloud has a real
  // id (legacy_* ids from the schema-1 migration are matched by text, which
  // needs the full list). Accounts do one full load; saveUserData then sets the flag.
  // options.all also covers a device whose local mirror holds records older
  // than the window: those can only be matched against the full history.
  const idsUpgraded = !!(meta && meta.idsUpgraded);
  const windowStart = (options.all || !idsUpgraded) ? null : historyWindowStart();
  const completedRef = collection(db, 'users', uid, 'completed');
  const completedQuery = windowStart
    ? query(completedRef, where('completedOn', '>=', windowStart))   // completedOn is an ISO string: lexical order = time order
    : completedRef;

  let totals = (meta && meta.totals && typeof meta.totals.count === 'number' && typeof meta.totals.seconds === 'number')
    ? { count: meta.totals.count, seconds: meta.totals.seconds } : null;

  const [taskSnap, doneSnap] = await Promise.all([
    getDocs(collection(db, 'users', uid, 'tasks')),
    getDocs(completedQuery)
  ]);
  const tasks = taskSnap.docs.map(d => normalizeTask(d.data(), d.id)).filter(Boolean).sort(byCreated);
  const completed = doneSnap.docs.map(d => normalizeCompleted(d.data(), d.id)).filter(Boolean).sort(byCompleted);

  // First load on schema 2 without totals: compute them once from the full history and store them.
  if (!totals) {
    const allDone = windowStart ? (await getDocs(completedRef)).docs.map(d => normalizeCompleted(d.data(), d.id)).filter(Boolean) : completed;
    totals = totalsOf(allDone);
    await setDoc(doc(db, 'users', uid, 'fishbowl', 'meta'), { totals }, { merge: true });
    console.log('Initialised all-time totals:', totals);
  } else if (!windowStart && !options.all) {
    // Full load on the main page: the counter can be checked against the
    // documents themselves. Correct it if it drifted (it is derived data).
    const actual = totalsOf(completed);
    if (actual.count !== totals.count || actual.seconds !== totals.seconds) {
      console.warn('All-time totals corrected from history:', totals, '->', actual);
      totals = actual;
      await setDoc(doc(db, 'users', uid, 'fishbowl', 'meta'), { totals }, { merge: true });
    }
  }

  const m = metaSubset(meta);
  m.manualQueue = m.manualQueue.map((q, i) => normalizeTask(q, legacyId('queue', q, i))).filter(Boolean);

  resetSynced(uid);
  tasks.forEach(t => synced.tasks.set(t.id, JSON.stringify(plain(t))));
  completed.forEach(c => synced.completed.set(c.id, JSON.stringify(plain(c))));
  synced.meta = JSON.stringify(plain(m));
  synced.streakInfo = m.streakInfo;
  synced.totals = { ...totals };
  synced.windowStart = windowStart;
  synced.flagIdsUpgraded = !idsUpgraded;

  return {
    tasks, completed, tags: m.tags, manualQueue: m.manualQueue, streakInfo: m.streakInfo, active: m.active, deletedIds: m.deletedIds,
    totals: { ...totals },
    historyWindowDays: windowStart ? HISTORY_WINDOW_DAYS : 0,
    historyWindowStart: windowStart || ''
  };
}

function totalsOf(list) {
  let count = 0, seconds = 0;
  for (const c of list) { count++; const s = durationSecOf(c); if (s !== null) seconds += s; }
  return { count, seconds };
}

// ✅ Exported: drop completed records older than the loaded window from the
// in-memory list once they are known to be in the cloud (called right after a
// save). They are also forgotten by the diff engine so they can never be
// mistaken for deletions.
export function trimCompletedToWindow(list) {
  if (!synced.uid) return list;
  // After a full load every cloud record is in `synced`, so trimming to the
  // window is safe then too (and keeps the mirror small, so the next login
  // does not need another full load).
  const windowStart = synced.windowStart || historyWindowStart();
  const keep = [], drop = [];
  for (const c of list) ((c.completedOn || '') < windowStart && synced.completed.has(c.id) ? drop : keep).push(c);
  drop.forEach(c => synced.completed.delete(c.id));
  return keep;
}

// ✅ Exported: every completed record, regardless of the history window
// (used by the data export and the stats page).
export async function loadAllCompleted() {
  const uid = getUID();
  if (!uid) return [];
  const snap = await getDocs(collection(db, 'users', uid, 'completed'));
  return snap.docs.map(d => normalizeCompleted(d.data(), d.id)).filter(Boolean).sort(byCompleted);
}

// ✅ Exported: Save user data (diff-based).
// data = { tasks, completed, tags, manualQueue, active, streakInfo?, deletedIds? }
export async function saveUserData(data) {
  const uid = getUID();
  if (!uid) return;
  if (synced.uid !== uid) {
    // Never sync against a cloud state we have not loaded — that is how data gets wiped.
    console.warn('saveUserData skipped: cloud data not loaded yet for this user.');
    return;
  }

  const ops = [];
  const nextTasks = new Map();
  (data.tasks || []).forEach(t => {
    const n = normalizeTask(t); if (!n || !n.id) return;
    const key = JSON.stringify(plain(n));
    nextTasks.set(n.id, key);
    if (synced.tasks.get(n.id) !== key) ops.push(['set', 'tasks', n.id, plain(n)]);
  });
  for (const id of synced.tasks.keys()) if (!nextTasks.has(id)) ops.push(['delete', 'tasks', id]);

  const nextDone = new Map();
  let dCount = 0, dSeconds = 0; // all-time totals delta for this save
  const candidates = [];
  (data.completed || []).forEach(c => {
    const n = normalizeCompleted(c); if (!n || !n.id) return;
    candidates.push(n);
    nextDone.set(n.id, JSON.stringify(plain(n)));
  });
  // A record older than the loaded history window that we have not seen may
  // already exist in the cloud (only the recent window was loaded). Check
  // before treating it as new, so it is neither re-uploaded nor double-counted.
  const unseenOld = candidates.filter(n => synced.completed.get(n.id) === undefined && synced.windowStart && (n.completedOn || '') < synced.windowStart);
  if (unseenOld.length) {
    const snaps = await Promise.all(unseenOld.map(n => getDoc(doc(db, 'users', uid, 'completed', n.id))));
    snaps.forEach((snap, i) => {
      if (snap.exists()) synced.completed.set(unseenOld[i].id, JSON.stringify(plain(normalizeCompleted(snap.data(), unseenOld[i].id))));
    });
  }
  candidates.forEach(n => {
    const key = nextDone.get(n.id);
    const prev = synced.completed.get(n.id);
    if (prev !== key) {
      ops.push(['set', 'completed', n.id, plain(n)]);
      const secNow = durationSecOf(n) || 0;
      if (prev === undefined) { dCount += 1; dSeconds += secNow; }
      else { dSeconds += secNow - (durationSecOf(JSON.parse(prev)) || 0); }
    }
  });
  for (const [id, prev] of synced.completed.entries()) {
    if (!nextDone.has(id)) { ops.push(['delete', 'completed', id]); dCount -= 1; dSeconds -= durationSecOf(JSON.parse(prev)) || 0; }
  }

  const meta = metaSubset({
    tags: data.tags,
    manualQueue: (data.manualQueue || []).map(normalizeTask).filter(Boolean),
    active: data.active,
    streakInfo: data.streakInfo !== undefined ? data.streakInfo : synced.streakInfo,
    deletedIds: data.deletedIds
  });
  const metaKey = JSON.stringify(plain(meta));
  const metaChanged = metaKey !== synced.meta;
  const totalsChanged = dCount !== 0 || dSeconds !== 0;
  const flagUpgrade = !!synced.flagIdsUpgraded;
  if (metaChanged || totalsChanged || flagUpgrade) {
    // One write to meta per save. Totals use atomic increments, so two devices
    // completing tasks at the same time cannot overwrite each other's count.
    const payload = metaChanged ? plain(meta) : {};
    if (totalsChanged) payload.totals = { count: increment(dCount), seconds: increment(dSeconds) };
    if (flagUpgrade) payload.idsUpgraded = true; // the full-history merge has been saved
    ops.push(['merge', 'fishbowl', 'meta', payload, { metaChanged, totalsChanged, flagUpgrade }]);
  }

  if (ops.length === 0) return;
  console.log(`Saving ${ops.length} change(s) for uid:`, uid);

  for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    ops.slice(i, i + BATCH_LIMIT).forEach(([op, coll, id, payload]) => {
      const ref = doc(db, 'users', uid, coll, id);
      if (op === 'set') batch.set(ref, payload);
      else if (op === 'merge') batch.set(ref, payload, { merge: true });
      else batch.delete(ref);
    });
    await batch.commit();
    // Record what is now in the cloud so the next diff is exact.
    ops.slice(i, i + BATCH_LIMIT).forEach(([op, coll, id, payload, flags = {}]) => {
      if (coll === 'tasks') op === 'delete' ? synced.tasks.delete(id) : synced.tasks.set(id, JSON.stringify(payload));
      else if (coll === 'completed') op === 'delete' ? synced.completed.delete(id) : synced.completed.set(id, JSON.stringify(payload));
      else if (coll === 'fishbowl') {
        if (flags.metaChanged) { synced.meta = metaKey; synced.streakInfo = meta.streakInfo; }
        if (flags.flagUpgrade) synced.flagIdsUpgraded = false;
        if (flags.totalsChanged) synced.totals = { count: (synced.totals?.count || 0) + dCount, seconds: (synced.totals?.seconds || 0) + dSeconds };
      }
    });
  }
}

// ✅ Exported: delete every Firestore document belonging to the current user
// (tasks, completed, meta, and the legacy state doc). Used by account deletion.
// The usage log (AllLogs) is pseudonymous and cannot be deleted from the client.
export async function deleteAllUserData() {
  const uid = getUID();
  if (!uid) return;
  const refs = [];
  for (const coll of ['tasks', 'completed']) {
    const snap = await getDocs(collection(db, 'users', uid, coll));
    snap.docs.forEach(d => refs.push(doc(db, 'users', uid, coll, d.id)));
  }
  refs.push(doc(db, 'users', uid, 'fishbowl', 'meta'));
  refs.push(doc(db, 'users', uid, 'fishbowl', 'state'));
  for (let i = 0; i < refs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    refs.slice(i, i + BATCH_LIMIT).forEach(r => batch.delete(r));
    await batch.commit();
  }
  resetSynced(null);
  console.log(`Deleted ${refs.length} document(s) for uid ${uid}`);
}

// ✅ Unified logging for all users. Every visitor is signed in (anonymously at
// least), so the log is keyed by the pseudonymous user id. Email is not stored.
export async function logEvent(action, taskText, duration) {
  const uid = getUID();
  if (!uid) return; // not signed in yet (anonymous sign-in still in flight)
  const timestamp = new Date().toISOString();

  const docData = {
    user: uid,
    anonymous: !!auth.currentUser?.isAnonymous,
    type: action,
    task: taskText,
    timestamp
  };
  if (duration !== undefined && duration !== null) {
    docData.duration = duration;
  }

  try {
    await addDoc(collection(db, "AllLogs"), docData);
  } catch (e) {
    console.warn("❌ Failed to log event:", e);
  }
}

// ✅ Also export auth if needed elsewhere
export { auth, onAuthStateChanged };
